import React, { useMemo } from 'react';

import { t, Trans } from '@lingui/macro';
import { SearchInput, Spinner, Table3 } from '@economic/taco';
import View, { useViewFilter } from '~/app/shared/components/View/View';

import { DimensionLine, DimensionLineType } from '../types';

export interface DimensionLineListViewProps<T = object> {
    dimensionLines: DimensionLine[];
    enableCreate?: boolean;
    onCreated?: (data?: any) => void;
    onSelected: (data: T) => void;
    includeDistributionKeys: boolean;
}

const DimensionLinesListView = ({
    dimensionLines: data,
    onSelected,
    includeDistributionKeys,
}: DimensionLineListViewProps<DimensionLine>) => {
    const [filter, setFilter, filterBy] = useViewFilter();
    const filteredData = useMemo(() => {
        return typeof data === 'undefined'
            ? []
            : data.filter(value => value.active && (filterBy(value.name) || filterBy(value.key)));
    }, [data, filter]);

    if (typeof data === 'undefined') return <Spinner />;

    return (
        <View>
            <View.Title>
                <Trans>Dimension values</Trans>
            </View.Title>
            <View.Header>
                <View.Header.Filters>
                    <SearchInput
                        autoFocus
                        onChange={e => setFilter(e.target.value)}
                        placeholder={t`Search for no. or name`}
                        value={filter}
                    />
                </View.Header.Filters>
            </View.Header>

            <div className="flex flex-col w-full">
                <Table3<DimensionLine> id="dimension-value-list" data={filteredData} onRowClick={row => onSelected(row)}>
                    <Table3.Column accessor="key" header={t`No.`} defaultWidth={75} sort="asc" />
                    <Table3.Column accessor="name" align="left" header={t`Name`} />
                    {includeDistributionKeys && (
                        <Table3.Column
                            accessor="type"
                            align="right"
                            header={t`Type`}
                            renderer={value =>
                                value === DimensionLineType.DimensionValue ? t`Dimension value` : t`Distribution key`
                            }
                        />
                    )}
                </Table3>
            </div>
        </View>
    );
};

export default DimensionLinesListView;
